import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceStatusDashboard = ({ className = '' }) => {
  const [complianceData, setComplianceData] = useState({});
  const [selectedTimeframe, setSelectedTimeframe] = useState('current');

  useEffect(() => {
    const mockComplianceData = {
      gmpAdherence: {
        overall: 94.2,
        categories: [
          { name: 'Documentation', score: 96.8, status: 'compliant' },
          { name: 'Personnel Training', score: 92.5, status: 'compliant' },
          { name: 'Equipment Validation', score: 89.3, status: 'warning' },
          { name: 'Quality Control', score: 97.1, status: 'compliant' }
        ]
      },
      pendingSubmissions: [
        {
          id: 'SUB-2024-001',
          product: 'Cardiomax 50mg',
          type: 'Marketing Authorization',
          authority: 'EMA',
          deadline: new Date('2024-10-15'),
          status: 'in_progress',
          completion: 78
        },
        {
          id: 'SUB-2024-002',
          product: 'Neurofen Plus',
          type: 'Variation Application',
          authority: 'MHRA',
          deadline: new Date('2024-09-30'),
          status: 'review',
          completion: 95
        },
        {
          id: 'SUB-2024-003',
          product: 'Diabetrol XR',
          type: 'Annual Report',
          authority: 'BfArM',
          deadline: new Date('2024-11-20'),
          status: 'draft',
          completion: 45
        }
      ],
      auditFindings: {
        total: 12,
        critical: 1,
        major: 4,
        minor: 7,
        resolved: 8,
        pending: 4
      },
      blockchainVerification: {
        totalDocuments: 1847,
        verified: 1845,
        pending: 2,
        lastVerification: new Date('2024-09-19T14:30:00')
      }
    };

    setComplianceData(mockComplianceData);
  }, [selectedTimeframe]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-success/10';
      case 'warning': return 'bg-warning/10';
      case 'critical': return 'bg-error/10';
      default: return 'bg-muted';
    }
  };

  const getSubmissionStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return 'text-primary';
      case 'review': return 'text-warning';
      case 'draft': return 'text-muted-foreground';
      case 'submitted': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatDeadline = (date) => {
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `${diffDays} days remaining`;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Timeframe Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Compliance Status Dashboard</h2>
          <p className="text-sm text-muted-foreground">Real-time regulatory compliance monitoring</p>
        </div>
        <select
          value={selectedTimeframe}
          onChange={(e) => setSelectedTimeframe(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
        >
          <option value="current">Current Period</option>
          <option value="quarterly">Quarterly View</option>
          <option value="annual">Annual Overview</option>
        </select>
      </div>
      {/* GMP Adherence Metrics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-card-foreground">GMP Adherence Metrics</h3>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-2xl font-bold text-success">{complianceData?.gmpAdherence?.overall}%</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {complianceData?.gmpAdherence?.categories?.map((category, index) => (
            <div key={index} className={`p-4 rounded-lg border ${getStatusBgColor(category?.status)}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-card-foreground">{category?.name}</h4>
                <Icon 
                  name={category?.status === 'compliant' ? 'CheckCircle' : 'AlertTriangle'} 
                  size={16} 
                  className={getStatusColor(category?.status)}
                />
              </div>
              <div className="text-2xl font-bold text-card-foreground mb-1">{category?.score}%</div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${category?.status === 'compliant' ? 'bg-success' : 'bg-warning'}`}
                  style={{ width: `${category?.score}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Pending Regulatory Submissions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-card-foreground">Pending Regulatory Submissions</h3>
          <span className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-full">
            {complianceData?.pendingSubmissions?.length} Active
          </span>
        </div>
        
        <div className="space-y-4">
          {complianceData?.pendingSubmissions?.map((submission) => (
            <div key={submission?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors duration-150">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-card-foreground">{submission?.product}</h4>
                  <p className="text-sm text-muted-foreground">{submission?.type} - {submission?.authority}</p>
                  <p className="text-xs text-muted-foreground mt-1">ID: {submission?.id}</p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${getSubmissionStatusColor(submission?.status)}`}>
                    {submission?.status?.replace('_', ' ')?.toUpperCase()}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDeadline(submission?.deadline)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{submission?.completion}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="h-2 bg-primary rounded-full transition-all duration-300"
                      style={{ width: `${submission?.completion}%` }}
                    ></div>
                  </div>
                </div>
                <button className="px-3 py-1 text-xs font-medium text-primary hover:bg-primary/5 border border-primary rounded-md transition-colors duration-150">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Audit Findings & Blockchain Verification */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Audit Findings */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-card-foreground mb-4">Audit Findings</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Findings</span>
              <span className="text-2xl font-bold text-card-foreground">{complianceData?.auditFindings?.total}</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-error rounded-full"></div>
                  <span className="text-sm text-card-foreground">Critical</span>
                </div>
                <span className="text-sm font-medium text-error">{complianceData?.auditFindings?.critical}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-warning rounded-full"></div>
                  <span className="text-sm text-card-foreground">Major</span>
                </div>
                <span className="text-sm font-medium text-warning">{complianceData?.auditFindings?.major}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                  <span className="text-sm text-card-foreground">Minor</span>
                </div>
                <span className="text-sm font-medium text-primary">{complianceData?.auditFindings?.minor}</span>
              </div>
            </div>
            
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Resolution Status</span>
                <span className="text-sm text-success font-medium">
                  {complianceData?.auditFindings?.resolved}/{complianceData?.auditFindings?.total} Resolved
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Blockchain Verification */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-card-foreground mb-4">Blockchain Verification</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Documents</span>
              <span className="text-2xl font-bold text-card-foreground">
                {complianceData?.blockchainVerification?.totalDocuments?.toLocaleString()}
              </span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-card-foreground">Verified</span>
                </div>
                <span className="text-sm font-medium text-success">
                  {complianceData?.blockchainVerification?.verified?.toLocaleString()}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-warning" />
                  <span className="text-sm text-card-foreground">Pending</span>
                </div>
                <span className="text-sm font-medium text-warning">
                  {complianceData?.blockchainVerification?.pending}
                </span>
              </div>
            </div>
            
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Verification</span>
                <span className="text-sm text-card-foreground font-mono">
                  {complianceData?.blockchainVerification?.lastVerification?.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-success/5 rounded-lg">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm text-success font-medium">All critical documents verified</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStatusDashboard;